import { useQuery } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import { MOTORES } from '@remoto/protocolo';
import { chamar } from '../api';

/** O mínimo que se precisa saber de uma linha para achar a mestre. */
export interface LinhaComFuncao {
  funcao?: string;
  estado: string;
  /** Com dois motores há duas mestres, uma de cada. Ausente = CLAUDE. */
  motor?: string;
}

/**
 * Esta é a mestre vigente?
 *
 * Só uma existe por vez, e o banco garante isso com índice parcial único
 * (`sessoes_claude_mestre_vigente_idx`, sobre `funcao = 'MESTRE'` e estado
 * diferente de `ENCERRADA`). Este predicado é a mesma condição do lado de cá —
 * e por isso `ENCERRADA` entra: uma mestre encerrada não é a vigente, e a
 * próxima já pode ter nascido.
 *
 * Virou função porque estava escrita **duas vezes** dentro da lista (uma para
 * fixar no topo, outra para tirar dos grupos) e agora a Prosa precisa dela
 * também. Três cópias da mesma condição é como uma delas envelhece sozinha.
 */
export function ehMestreVigente(linha: LinhaComFuncao): boolean {
  return linha.funcao === 'MESTRE' && linha.estado !== 'ENCERRADA';
}

/**
 * As mestres vigentes — UMA POR MOTOR, e a lista pode ter as duas.
 *
 * `acharMestre` (singular) devolvia a primeira, e `ehMestreVigente` tira TODAS
 * dos grupos. Com dois mestres isso fazia a segunda **sumir da tela**: não
 * estava nos grupos (foi removida) nem no topo (só cabia uma). A sessão
 * existia, respondia, ocupava vaga — e o painel não a mostrava.
 *
 * A ordem é a dos motores (`MOTORES`), e não a da lista: a posição de um
 * cartão fixo não pode mudar porque o banco devolveu noutra ordem.
 */
export function acharMestres<T extends LinhaComFuncao>(linhas: readonly T[]): T[] {
  const vigentes = linhas.filter(ehMestreVigente);
  return MOTORES.map((motor) =>
    vigentes.find((linha) => (linha.motor ?? 'CLAUDE') === motor),
  ).filter((linha): linha is T => linha !== undefined);
}

/**
 * A mestre de um motor, ou nada.
 *
 * Existe porque quase todo chamador quer UMA: a Prosa fala com a que a pessoa
 * escolheu, o cutucão é do motor da sessão. Sem motor, a do Claude — que é o
 * padrão de toda a casa.
 */
export function acharMestre<T extends LinhaComFuncao>(
  linhas: readonly T[],
  motor: string = 'CLAUDE',
): T | undefined {
  return linhas.find((linha) => ehMestreVigente(linha) && (linha.motor ?? 'CLAUDE') === motor);
}

interface LinhaDaFrota extends LinhaComFuncao {
  id: string;
  nome?: string | null;
}

/**
 * A sessão mestre vigente, ou nada.
 *
 * Usa a **mesma chave de consulta** da lista de sessões (`['sessoes', false]`),
 * então abrir a Prosa não pede a lista de novo: quem já tinha o painel aberto
 * lê do cache, e quem chega direto na Prosa paga uma requisição que a lista
 * depois aproveita.
 *
 * `undefined` tem dois significados que a tela precisa distinguir, e é por isso
 * que `carregando` sai junto: ainda não sei, ou não existe. Sem essa diferença
 * a Prosa piscaria "não há mestre" durante o primeiro carregamento — e a
 * ausência de mestre não é erro, é ausência: quem não é ADMIN nunca a vê,
 * porque o servidor a esconde no `filtroSessoes`.
 */
export function usarSessaoMestre(): {
  mestre: LinhaDaFrota | undefined;
  /** TODAS as vigentes, uma por motor — é o que a Prosa usa para escolher. */
  mestres: LinhaDaFrota[];
  carregando: boolean;
} {
  const { data: sessoes = [], isLoading } = useQuery({
    queryKey: ['sessoes', false],
    queryFn: () => chamar<LinhaDaFrota[]>(ROTAS.sessoes),
    refetchInterval: 10_000,
  });

  const mestres = acharMestres(sessoes);
  // `mestre` continua sendo a PRIMEIRA na ordem dos motores: quem só quer uma
  // não precisa saber que agora há duas.
  return { mestre: mestres[0], mestres, carregando: isLoading };
}
